import type { EquipmentNode } from './types.js';

/**
 * Equipment List'in ağaç modeli (M7a Task 6b; `gemi-pms-ekipman.jpeg` "Equipment Tree"): görünür
 * satırlar, arama, WAI-ARIA tree klavye gezintisi ve adres sorgusu. Saf modül: ekran yalnız çizer.
 */

export interface TreeRow {
  node: EquipmentNode;
  /** 1'den başlar (`aria-level`). */
  level: number;
  hasChildren: boolean;
  expanded: boolean;
  /** Aramayla eşleşti (vurgulanır). */
  match: boolean;
  /** Kardeşler arasındaki yeri (`aria-posinset`, `aria-setsize`). */
  position: number;
  setSize: number;
}

const byOrder = (a: EquipmentNode, b: EquipmentNode) =>
  a.sortOrder - b.sortOrder ||
  a.code.localeCompare(b.code, 'en', { numeric: true }) ||
  a.name.localeCompare(b.name, 'en');

/** Üst düğüme göre çocuklar, sırayla. Üstü ağaçta olmayan düğüm köktür. */
export function childrenOf(nodes: readonly EquipmentNode[]): Map<string | null, EquipmentNode[]> {
  const ids = new Set(nodes.map((n) => n.id));
  const map = new Map<string | null, EquipmentNode[]>();
  for (const node of nodes) {
    const parent = node.parentId !== null && ids.has(node.parentId) ? node.parentId : null;
    const list = map.get(parent) ?? [];
    list.push(node);
    map.set(parent, list);
  }
  for (const list of map.values()) list.sort(byOrder);
  return map;
}

/** Kökten üste ataların kimlikleri (kendisi hariç). */
export function ancestorIds(nodes: readonly EquipmentNode[], id: string): string[] {
  const byId = new Map(nodes.map((n) => [n.id, n]));
  const out: string[] = [];
  const seen = new Set<string>([id]);
  let cursor = byId.get(id)?.parentId ?? null;
  while (cursor !== null && !seen.has(cursor) && byId.has(cursor)) {
    seen.add(cursor);
    out.unshift(cursor);
    cursor = byId.get(cursor)?.parentId ?? null;
  }
  return out;
}

/** Bütün alt düğümler (her düzey). */
export function descendantIds(nodes: readonly EquipmentNode[], id: string): string[] {
  const children = childrenOf(nodes);
  const out: string[] = [];
  const stack = [...(children.get(id) ?? [])];
  const seen = new Set<string>([id]);
  while (stack.length > 0) {
    const next = stack.shift();
    if (!next || seen.has(next.id)) continue;
    seen.add(next.id);
    out.push(next.id);
    stack.push(...(children.get(next.id) ?? []));
  }
  return out;
}

/** Düğümün kökteki sistemi ("Equipment" satırı); kökse kendisi. */
export function rootOf(nodes: readonly EquipmentNode[], id: string): EquipmentNode | undefined {
  const first = ancestorIds(nodes, id)[0] ?? id;
  return nodes.find((n) => n.id === first);
}

/**
 * Arama katlaması (ADR 0028 #37; bağımsız inceleme M7A-SCR-1): NFKD, birleşik işaretler atılır,
 * `ı` → `i`, sonra kök yerelde küçük harf. Türkçe yerelle küçültme İngilizce adın büyük I'sını
 * noktasız ı yapardı ("Injectors" "inj" ile bulunmazdı); böylece "isik" hem "Işık"ı hem "ışık"ı,
 * "inj" "Injectors"ı bulur.
 */
export function foldSearchText(text: string): string {
  return text.normalize('NFKD').replace(/\p{M}/gu, '').replaceAll('ı', 'i').toLowerCase().trim();
}

function matches(node: EquipmentNode, query: string): boolean {
  return (
    foldSearchText(node.name).includes(query) ||
    foldSearchText(node.code).includes(query) ||
    (node.tag ? foldSearchText(node.tag).includes(query) : false)
  );
}

/** Açık-kapalı durumu: kullanıcının açtıkları ve (arama sürerken) kapattıkları. */
export interface TreeOpenState {
  expanded: ReadonlySet<string>;
  collapsed: ReadonlySet<string>;
}

/** Düğümü açar ya da kapatır; arama sürerken aramanın açtığı ata da kapanabilir (M7A-SCR-7). */
export function withOpen(
  state: TreeOpenState,
  id: string,
  open: boolean,
): { expanded: Set<string>; collapsed: Set<string> } {
  const expanded = new Set(state.expanded);
  const collapsed = new Set(state.collapsed);
  if (open) {
    expanded.add(id);
    collapsed.delete(id);
  } else {
    expanded.delete(id);
    collapsed.add(id);
  }
  return { expanded, collapsed };
}

/**
 * Görünür satırlar. Arama varken eşleşen düğümler, ataları ve alt düğümleri görünür; atalar
 * kendiliğinden açılır, eşleşmeyen dallar gizlenir. Arama sürerken de ok ve klavye açar-kapatır:
 * açık küme `(aramanın açtıkları ∪ expanded) − collapsed`. Aramasızken yalnız açık düğümlerin
 * çocukları görünür.
 */
export function treeRows(
  nodes: readonly EquipmentNode[],
  expanded: ReadonlySet<string>,
  query: string,
  collapsed: ReadonlySet<string> = new Set(),
  filter: { criticalOnly?: boolean } = {},
): TreeRow[] {
  const children = childrenOf(nodes);
  const q = foldSearchText(query);
  let visible: Set<string> | null = null;
  let open: ReadonlySet<string> = expanded;
  const matched = new Set<string>();
  // "Critical only" (K22): kritik işaretli ve miras alan düğümler, ataları açık [NR496 4.4]
  if (filter.criticalOnly) {
    visible = new Set<string>();
    const opened = new Set<string>(expanded);
    for (const node of nodes) {
      if (!node.effectiveCritical) continue;
      visible.add(node.id);
      for (const ancestor of ancestorIds(nodes, node.id)) {
        visible.add(ancestor);
        opened.add(ancestor);
      }
    }
    for (const id of collapsed) opened.delete(id);
    open = opened;
    if (visible.size === 0) return [];
  }
  if (q !== '') {
    const scope = visible;
    visible = new Set<string>();
    const opened = new Set<string>(open);
    for (const node of nodes) {
      if (scope && !scope.has(node.id)) continue;
      if (!matches(node, q)) continue;
      matched.add(node.id);
      visible.add(node.id);
      for (const ancestor of ancestorIds(nodes, node.id)) {
        visible.add(ancestor);
        opened.add(ancestor);
      }
      for (const descendant of descendantIds(nodes, node.id)) {
        if (!scope || scope.has(descendant)) visible.add(descendant);
      }
    }
    for (const id of collapsed) opened.delete(id);
    open = opened;
  }
  const rows: TreeRow[] = [];
  const walk = (parent: string | null, level: number) => {
    const list = (children.get(parent) ?? []).filter((n) => !visible || visible.has(n.id));
    list.forEach((node, index) => {
      const hasChildren = (children.get(node.id) ?? []).some((n) => !visible || visible.has(n.id));
      const isOpen = hasChildren && open.has(node.id);
      rows.push({
        node,
        level,
        hasChildren,
        expanded: isOpen,
        match: matched.has(node.id),
        position: index + 1,
        setSize: list.length,
      });
      if (isOpen) walk(node.id, level + 1);
    });
  };
  walk(null, 1);
  return rows;
}

export type TreeAction = {
  focus?: string;
  select?: string;
  /** Açılacak ya da kapanacak düğüm (`withOpen`). */
  toggle?: { id: string; open: boolean };
};

/**
 * WAI-ARIA tree klavyesi: ↓ ↑ sonraki/önceki görünür satır; → kapalıyı açar, açıkta ilk çocuğa
 * iner; ← açığı kapatır, kapalıda ya da yaprakta üst düğüme çıkar; Home/End ilk/son; Enter ve
 * boşluk seçer. Açıklık satırdan okunur (arama sürerken de doğru). Tanınmayan tuş `null`.
 */
export function treeKey(
  key: string,
  rows: readonly TreeRow[],
  focusedId: string,
): TreeAction | null {
  const index = rows.findIndex((r) => r.node.id === focusedId);
  const row = rows[index];
  if (!row) return null;
  switch (key) {
    case 'ArrowDown': {
      const next = rows[index + 1];
      return next ? { focus: next.node.id } : null;
    }
    case 'ArrowUp': {
      const previous = rows[index - 1];
      return previous ? { focus: previous.node.id } : null;
    }
    case 'ArrowRight': {
      if (!row.hasChildren) return null;
      if (!row.expanded) return { toggle: { id: row.node.id, open: true } };
      const child = rows[index + 1];
      return child ? { focus: child.node.id } : null;
    }
    case 'ArrowLeft': {
      if (row.hasChildren && row.expanded) return { toggle: { id: row.node.id, open: false } };
      for (let i = index - 1; i >= 0; i -= 1) {
        const candidate = rows[i];
        if (candidate && candidate.level < row.level) return { focus: candidate.node.id };
      }
      return null;
    }
    case 'Home': {
      const first = rows[0];
      return first ? { focus: first.node.id } : null;
    }
    case 'End': {
      const last = rows.at(-1);
      return last ? { focus: last.node.id } : null;
    }
    case 'Enter':
    case ' ':
      return { select: row.node.id };
    default:
      return null;
  }
}

/**
 * "Add Equipment"ın varsayılan üstü (M7A-SCR-4): seçili düğüm ya da bileşen olmayan en yakın
 * atası. Çekmecenin üst listesinde bileşen yoktur; seçili bileşen üst yapılırsa alan boş görünür
 * ama kayıt bileşenin altına yazılırdı.
 */
export function addParentFor(
  nodes: readonly EquipmentNode[],
  selectedId: string | null,
): EquipmentNode | null {
  if (selectedId === null) return null;
  const byId = new Map(nodes.map((n) => [n.id, n]));
  for (const id of [selectedId, ...ancestorIds(nodes, selectedId).reverse()]) {
    const node = byId.get(id);
    if (node && node.nodeType !== 'component') return node;
  }
  return null;
}

/** Adresteki ekipman ağaçta varsa o; yoksa ağacın ilk düğümü. */
export function defaultSelection(
  nodes: readonly EquipmentNode[],
  requested: string | null,
): string | null {
  if (requested && nodes.some((n) => n.id === requested)) return requested;
  return childrenOf(nodes).get(null)?.[0]?.id ?? null;
}

/**
 * İlk açılışta açık düğümler: seçilenin ataları, kendisi ve çocuklu alt düğümleri (tasarımda Main
 * Engine ve dört sistemi açık).
 */
export function defaultExpanded(nodes: readonly EquipmentNode[], selectedId: string | null) {
  if (selectedId === null) return new Set<string>();
  const children = childrenOf(nodes);
  const open = new Set(ancestorIds(nodes, selectedId));
  for (const id of [selectedId, ...descendantIds(nodes, selectedId)]) {
    if ((children.get(id) ?? []).length > 0) open.add(id);
  }
  return open;
}

export interface OverviewCounts {
  totalComponents: number;
  /** Vadesi gelen PM işleri; M7b'de iş tanımlarıyla dolar. */
  due: number;
  overdue: number;
  /** Son 30 günde tamamlanan; M7b'de iş kayıtlarıyla dolar. */
  completed: number;
}

/** Overview sayıları koddan (ADR 0024 #19): M7a'da iş yok, yalnız bileşen sayısı. */
export function overviewCounts(nodes: readonly EquipmentNode[], id: string): OverviewCounts {
  return { totalComponents: descendantIds(nodes, id).length, due: 0, overdue: 0, completed: 0 };
}

export interface EquipmentOption {
  value: string;
  label: string;
  /** Ağaçtaki yolu (atalar). */
  path: string;
}

/** Üst karttaki arama: ad · kod, ağaçtaki yoluyla (aynı adlı bileşenler ayırt edilir). */
export function equipmentOptions(nodes: readonly EquipmentNode[]): EquipmentOption[] {
  const byId = new Map(nodes.map((n) => [n.id, n]));
  const children = childrenOf(nodes);
  const ordered: EquipmentNode[] = [];
  const walk = (parent: string | null) => {
    for (const node of children.get(parent) ?? []) {
      ordered.push(node);
      walk(node.id);
    }
  };
  walk(null);
  return ordered.map((node) => ({
    value: node.id,
    label: `${node.name} · ${node.code}`,
    path: ancestorIds(nodes, node.id)
      .map((id) => byId.get(id)?.name ?? '')
      .join(' › '),
  }));
}

export interface PmsQuery {
  vessel: string | null;
  equipment: string | null;
}

/** Adres sorgusu: web'de seçili gemi `?vessel=`, seçili düğüm `?equipment=`. */
export function readPmsQuery(search: string | undefined): PmsQuery {
  const params = new URLSearchParams(search ?? '');
  return { vessel: params.get('vessel'), equipment: params.get('equipment') };
}

/**
 * Sorguyu günceller. Gemi değişince ekipman seçimi düşer (başka geminin düğümü olamaz); sorguda
 * başka anahtar tutulmaz.
 */
export function writePmsQuery(search: string | undefined, patch: Partial<PmsQuery>): string {
  const current = readPmsQuery(search);
  const vessel = patch.vessel !== undefined ? patch.vessel : current.vessel;
  const vesselChanged = patch.vessel !== undefined && patch.vessel !== current.vessel;
  const equipment =
    patch.equipment !== undefined ? patch.equipment : vesselChanged ? null : current.equipment;
  const params = new URLSearchParams();
  if (vessel) params.set('vessel', vessel);
  if (equipment) params.set('equipment', equipment);
  const text = params.toString();
  return text ? `?${text}` : '';
}
